/**
 * Draft lottery and draft order.
 *
 * The league does NOT assign draft slots directly from the lottery. Since 2020-II-1 the
 * lottery determines the ORDER IN WHICH GMs CHOOSE their preferred first-round slot. Win the
 * lottery and you pick where you want to draft; you do not automatically get 1.01.
 *
 * 2014-II-1   — lottery created, odds by finishing position.
 * 2015-II-4   — Toilet Bowl winner keeps his #2-or-#9 right regardless of the lottery.
 * 2018-II-5   — 3rd place may swap picks with 4th, declarable after the lottery.
 * 2020-II-1   — GMs select their preferred slot in lottery order; Toilet Bowl winner is
 *               guaranteed the 2nd selection priority unless he won the lottery outright.
 */

/**
 * 2014-II-1 §1. Keyed by PLACE — but "place" means two different things depending on where in
 * the table you are, and that is the whole of Pass 06 §1.
 *
 *   places 1-6   playoff outcomes. Champion, runner-up, the 3rd-place game's winner and loser,
 *                the 5th-place game's winner and loser. Already correct.
 *   places 7-10  the REGULAR SEASON. The four teams that missed the playoffs are re-ordered by
 *                the consolation bracket in Yahoo's final standings, and that re-ordering has
 *                nothing to do with what the odds are meant to reward.
 *
 * The two disagree on all four non-playoff teams — in 2025, on every one of them. `lotteryBasis`
 * below is the only place they are combined, and `season2025.ts` is the only place they are read.
 */
export const LOTTERY_ODDS: Readonly<Record<number, number>> = {
  10: 40,
  9: 20,
  8: 13,
  7: 10,
  6: 6,
  5: 4,
  4: 3,
  3: 2,
  2: 1,
  1: 1,
};

export function oddsSum(): number {
  return Object.values(LOTTERY_ODDS).reduce((a, b) => a + b, 0);
}

/**
 * THE ORDER THE ODDS ARE KEYED TO — playoff places on top, regular-season places underneath.
 *
 * `runLottery` weights by index, so whatever array reaches it IS the odds table. Handing it the
 * final standings gives the four non-playoff GMs each other's weights, which in 2025 means every
 * single one of them is wrong: Ryan drew at 13% when the rules give him 40%, John at 40% when
 * they give him 20%.
 *
 * `playoffTeams` rather than a hardcoded 6, because the league has changed the size of its
 * bracket before and the seam between the two bases is exactly that number.
 *
 * NOT RE-DERIVED FROM W-L AND POINTS-FOR. Both orderings arrive already ranked — Yahoo applied
 * its own tiebreakers years ago — and re-sorting them here would be the app second-guessing the
 * league's own record with an algorithm nobody voted on.
 */
export function lotteryBasis(
  finalOrder: readonly string[],
  regularSeasonOrder: readonly string[],
  playoffTeams: number,
): string[] {
  const top = finalOrder.slice(0, playoffTeams);
  /*
   * The tail is taken from the regular-season order IN ITS OWN SEQUENCE, filtered to whoever is
   * not already placed by a playoff outcome — rather than sliced at `playoffTeams`. The two
   * orderings do not agree about who the bottom four ARE in the general case (a team can miss
   * the playoffs on a tiebreak it wins in the other table), and a slice would then both duplicate
   * and drop somebody. The filter cannot.
   */
  const placed = new Set(top.map((g) => g.toUpperCase()));
  const tail = regularSeasonOrder.filter((g) => !placed.has(g.toUpperCase()));
  return [...top, ...tail];
}

/**
 * THE CONSOLATION BRACKET IS CONTESTED BY THE TEAMS THAT MISSED THE PLAYOFFS, so its winner can
 * never hold a regular-season rank inside the bracket. If he does, one of two inputs is wrong —
 * either the Toilet Bowl winner or the regular-season order — and both feed the draw.
 *
 * Cheap, and it is a free check on the one input nobody had verified: `toiletBowlWinner` was
 * inferred from the shape of the final standings until the bracket itself was read on 24 August.
 */
export function toiletBowlRankOk(
  regularSeasonOrder: readonly string[],
  toiletBowlWinnerId: string | null | undefined,
  playoffTeams: number,
): boolean {
  if (!toiletBowlWinnerId) return true;
  const rank = regularSeasonOrder
    .findIndex((g) => g.toUpperCase() === toiletBowlWinnerId.toUpperCase()) + 1;
  return rank > playoffTeams;
}

/**
 * Run the lottery. Draws without replacement, weighted by the published odds, to produce a
 * full selection order.
 *
 * `rng` must return a float in [0, 1). Inject a seeded generator to make a draft reproducible
 * and auditable — the league requires the lottery to happen in a live setting (2014-II-1 §5),
 * so the app should record the seed alongside the result.
 */
export function runLottery(
  finishOrder: readonly string[], // index 0 = champion ... index 9 = last place
  rng: () => number,
): string[] {
  const pool = finishOrder.map((teamId, i) => ({
    teamId,
    weight: LOTTERY_ODDS[i + 1] ?? 0,
  }));
  const order: string[] = [];

  while (pool.length > 0) {
    const total = pool.reduce((s, p) => s + p.weight, 0);
    if (total <= 0) {
      // Remaining teams all have zero weight; append in finishing order.
      order.push(...pool.map((p) => p.teamId));
      break;
    }
    let roll = rng() * total;
    let idx = pool.length - 1;
    for (let i = 0; i < pool.length; i++) {
      roll -= pool[i]!.weight;
      if (roll <= 0) {
        idx = i;
        break;
      }
    }
    order.push(pool[idx]!.teamId);
    pool.splice(idx, 1);
  }
  return order;
}

/**
 * 2020-II-1 §3: the Toilet Bowl winner is guaranteed the SECOND selection priority — unless
 * he won the lottery, in which case the provision is unused.
 */
export function applyToiletBowlPriority(
  lotteryOrder: readonly string[],
  toiletBowlWinnerId: string | null,
): string[] {
  const order = [...lotteryOrder];
  if (!toiletBowlWinnerId) return order;
  const current = order.indexOf(toiletBowlWinnerId);
  if (current <= 1) return order; // already 1st (won lottery) or already 2nd
  order.splice(current, 1);
  order.splice(1, 0, toiletBowlWinnerId);
  return order;
}

/**
 * WHICH LISTS THE RESOLVE READS — the one place that answers it.
 *
 * `resolveDraftSlots` below is given a map and asks no questions about where it came from, which
 * left the answer to each caller, and the callers answered it differently.
 *
 * SUBMITTED ONLY. Tim, 31 August: *"draft pick prefs were resolved into the order but shouldn't
 * have been."* A list a manager autosaved and never sent is a manager still thinking, and §5 sets
 * a time by which a ranking is DUE — a draft sitting in the box at 5:01 is not one he handed in.
 * §6 is the rule for the man who did not: the earliest slot still open at his turn, which is a
 * stated outcome rather than a penalty.
 *
 * `submittedAt` is a separate fact from `ranked` precisely because the 2026 screen autosaves as
 * the list is dragged. "Has a ranking" stopped meaning "has submitted one" the moment the first
 * tap started writing, and this is the only function allowed to conflate them, by refusing to.
 *
 * Every surface that counts rankings follows this: `rankingCensus.willResolve`, the "Rankings in"
 * strip, the ledger's third mark. If the rule ever moves, it moves here and they follow.
 */
export function rankingsForResolve(
  prefs: ReadonlyArray<{ gm: string; ranked: readonly number[]; submittedAt?: string | null }>,
): Record<string, readonly number[]> {
  const out: Record<string, readonly number[]> = {};
  for (const p of prefs) out[p.gm.toUpperCase()] = p.submittedAt ? p.ranked : [];
  return out;
}

/**
 * 2020-II-1 §4-§6: each GM submits a ranked list of first-round slots. Walking the selection
 * order, each GM receives his highest-ranked slot still available. A GM who submits nothing
 * gets the earliest remaining slot (§6).
 */
export function resolveDraftSlots(
  selectionOrder: readonly string[],
  preferences: Readonly<Record<string, readonly number[]>>,
  teams: number,
): Map<string, number> {
  const available = new Set(Array.from({ length: teams }, (_, i) => i + 1));
  const assigned = new Map<string, number>();

  for (const teamId of selectionOrder) {
    const prefs = preferences[teamId] ?? [];
    let chosen: number | undefined = prefs.find((slot) => available.has(slot));
    if (chosen === undefined) {
      // §6: no submission (or every preference taken) -> earliest remaining slot.
      chosen = Math.min(...available);
    }
    assigned.set(teamId, chosen);
    available.delete(chosen);
  }
  return assigned;
}

/*
 * THE THIRD-PLACE OPTION USED TO LIVE HERE, as `applyThirdFourthSwap(slots, …)` — a swap of two
 * entries in the RESOLVED SLOT MAP. Pass 06 §3 rules that the right trades lottery RESULTS rather
 * than slots: 3rd takes 4th's turn in the choosing order and then picks from his own list at that
 * turn, which is a different answer whenever the two men want different slots.
 *
 * It moved to `thirdPlaceSwap.ts` rather than changing shape here, because it is no longer a
 * function of the board at all — it is a declaration with four states, a deadline and an audit
 * trail. Deleted rather than deprecated: a slot-swapping helper left in this file is one autocomplete
 * away from being called again, and it would silently produce the superseded mechanic.
 */

/**
 * Expand first-round slots into a full snake draft board.
 * 2022 "Total Minimum Number of Picks Per GM": every GM must make exactly `rounds` selections,
 * so any pick trade has to return an equal number of picks. `validatePickCounts` enforces it.
 */
export interface DraftPick {
  overall: number;
  round: number;
  slot: number;
  /** Team that currently owns the pick — may differ from the slot's team after trades. */
  ownerTeamId: string;
  /** Team the slot originally belonged to. */
  originalTeamId: string;
}

export function buildSnakeBoard(
  slotAssignments: ReadonlyMap<string, number>,
  rounds: number,
): DraftPick[] {
  const bySlot = new Map<number, string>();
  for (const [teamId, slot] of slotAssignments) bySlot.set(slot, teamId);
  const teams = slotAssignments.size;

  const picks: DraftPick[] = [];
  let overall = 1;
  for (let round = 1; round <= rounds; round++) {
    const order =
      round % 2 === 1
        ? Array.from({ length: teams }, (_, i) => i + 1)
        : Array.from({ length: teams }, (_, i) => teams - i);
    for (const slot of order) {
      const teamId = bySlot.get(slot);
      if (!teamId) throw new Error(`No team assigned to draft slot ${slot}`);
      picks.push({ overall, round, slot, ownerTeamId: teamId, originalTeamId: teamId });
      overall++;
    }
  }
  return picks;
}

/**
 * 2022 "Total Minimum Number of Picks Per GM" §1-2: after all trades, every GM must still
 * hold exactly one pick per round's worth of selections. The farm-system selection is
 * supplemental and does not count (§3).
 */
export function validatePickCounts(
  picks: readonly DraftPick[],
  rounds: number,
): { legal: boolean; errors: string[] } {
  const counts = new Map<string, number>();
  for (const p of picks) counts.set(p.ownerTeamId, (counts.get(p.ownerTeamId) ?? 0) + 1);

  const errors: string[] = [];
  for (const [teamId, n] of counts) {
    if (n !== rounds) {
      errors.push(
        `${teamId} holds ${n} picks but must hold exactly ${rounds} — every GM leaves the ` +
          'draft having made one selection per round (2022 "Total Minimum Number of Picks ' +
          'Per GM" §1). Any pick trade must return an equal number of picks (§2).',
      );
    }
  }
  return { legal: errors.length === 0, errors };
}

/**
 * Remove picks forfeited to keeper declarations. A keeper costs the pick in his penalty round;
 * where two keepers land on the same round the cost "waterfalls" up to the next earlier
 * available round (2015-III-7).
 *
 * Returns the rounds actually forfeited, in ascending order.
 */
export function applyKeeperWaterfall(
  costRounds: readonly number[],
  rounds: number,
): { forfeited: number[]; errors: string[] } {
  const taken = new Set<number>();
  const errors: string[] = [];

  // Process earliest-costing keepers first so the waterfall is deterministic.
  for (const cost of [...costRounds].sort((a, b) => a - b)) {
    if (cost < 1 || cost > rounds) {
      errors.push(`Keeper cost of Round ${cost} is outside a ${rounds}-round draft.`);
      continue;
    }
    let round = cost;
    // 2015-III-7: "two 15th and one 14th... move one 15th to 14, then the 14th to 13"
    // i.e. collisions push toward the EARLIER round.
    while (round >= 1 && taken.has(round)) round--;
    if (round < 1) {
      errors.push(
        `Keeper costs waterfalled past Round 1 — this GM has declared more early-round ` +
          'keepers than he has picks to forfeit (2015-III-7).',
      );
      continue;
    }
    taken.add(round);
  }

  return { forfeited: [...taken].sort((a, b) => a - b), errors };
}

/** Mulberry32 — small seeded PRNG so a lottery can be replayed and audited. */
export function seededRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Assign a real, owned pick to every declared keeper.
 *
 * applyKeeperWaterfall above answers "which rounds get consumed" for a GM who holds all his
 * picks. That is not the question a manager actually faces. He faces two complications at
 * once, and they interact:
 *
 *   1. Two of his keepers want the same round (2015-III-7). One has to move earlier.
 *   2. He traded a pick away, so the round his keeper is priced at does not exist for him.
 *      Same resolution — move earlier — but triggered by ownership, not by collision.
 *
 * And the choice of WHICH keeper moves is his, not the app's. The commissioner was explicit:
 * "you can effectively escalate whomever you want, based on their overlaps." So this takes an
 * optional `preferred` round per keeper and honours it whenever it is legal, falling back to
 * the cheapest legal pick for anything the manager has not pinned.
 *
 * "Legal" means three things: he owns the pick, nothing else has claimed it, and it is no
 * later than the keeper's cost round. That last one is the constraint people forget — moving
 * a keeper EARLIER is a penalty the rules impose, so it is always allowed; moving him later
 * would be paying less than the Grid says and is not.
 *
 * Rounds are a multiset, not a set: the equal-picks rule (2022) means a pick-for-pick trade
 * returns the same number of picks, so a GM can easily end up holding two in the same round.
 */
export function assignKeeperPicks(
  keepers: ReadonlyArray<{ id: string; costRound: number; preferred?: number | null }>,
  ownedRounds: readonly number[],
): { assigned: Map<string, number>; errors: string[] } {
  const assigned = new Map<string, number>();
  const errors: string[] = [];

  // Multiset of what is still available, highest round first — the cheapest pick a keeper can
  // legally be paid with is the LATEST round at or before his cost.
  const pool = [...ownedRounds].sort((a, b) => b - a);
  const take = (round: number) => {
    const i = pool.indexOf(round);
    if (i < 0) return false;
    pool.splice(i, 1);
    return true;
  };

  // Honour the manager's explicit choices first, so his pins are never displaced by a
  // keeper the algorithm happened to place earlier.
  for (const k of keepers) {
    const want = k.preferred ?? null;
    if (want === null) continue;
    if (want > k.costRound) {
      errors.push(
        `${k.id} cannot be paid with a Round ${want} pick — he costs Round ${k.costRound}, and ` +
          'a keeper can only be moved to an earlier round, never a later one (2015-III-7).',
      );
      continue;
    }
    if (!take(want)) {
      errors.push(
        `${k.id} is assigned a Round ${want} pick that is not available — either it was traded ` +
          'away or another keeper already has it.',
      );
      continue;
    }
    assigned.set(k.id, want);
  }

  // Then everything else, most expensive keeper first so the scarce early rounds go to the
  // players who actually need them.
  //
  // TIES BREAK ON THE NAME, and that is load-bearing rather than tidy. Two keepers priced into the
  // same round cannot both stand there, and 2015-III-7 leaves the choice of which one walks back
  // to the GM — so where he has not made one, the answer has to come from somewhere. Sorting only
  // on the round left it coming from the CALLER'S ORDER: the declaration screen feeds a roster
  // sorted by cost then name, this board fed one sorted by cost alone, and John's two Round 12s
  // came out Stafford 11 / Tuten 12 on one screen and Tuten 11 / Stafford 12 on the other. Same
  // rule, same picks, two answers. The tiebreak lives here so no caller can reintroduce it by
  // handing the resolver a different order.
  for (const k of [...keepers]
    .sort((a, b) => a.costRound - b.costRound || a.id.localeCompare(b.id))) {
    if (assigned.has(k.id)) continue;
    const round = pool.find((r) => r <= k.costRound);
    if (round === undefined) {
      errors.push(
        `${k.id} costs a Round ${k.costRound} pick and there is no pick at or before that round ` +
          'left to pay with. Drop a keeper, or trade for an earlier pick.',
      );
      continue;
    }
    take(round);
    assigned.set(k.id, round);
  }

  return { assigned, errors };
}

/**
 * Which PICK, not just which round.
 *
 * `assignKeeperPicks` above answers at the round level, and for most GMs that is the whole
 * answer — one pick per round, so naming the round names the pick. The equal-picks rule (2022)
 * breaks that: a pick-for-pick trade returns the same number of picks, so after the November
 * trade John holds two 9ths and two 11ths, and Rajan two 14ths and two 15ths. "His Round 9
 * pick" is now ambiguous, and a pick's NUMBER on the board comes from its original owner's
 * slot — John's own 9th and the one he got from Rajan are different cells.
 *
 * This resolves the ambiguity: every keeper gets a specific pick, identified by (round,
 * original owner). Identity, not code — a code needs the draft order, and slates are declared
 * before the lottery runs. The origin is the stable name for the cell.
 *
 * Defaults, in order:
 *   - the origin the manager chose (`preferredFrom`), when that pick is in his assigned round
 *     and unclaimed;
 *   - with the order known, the LATER pick within the round — spending it keeps the earlier,
 *     more valuable cell live for drafting, and it is the default the design specifies;
 *   - before the order exists, "later" is unknowable, so: his own pick. An acquired pick is
 *     one he traded FOR, presumably to draft with; his own is the one he expected to spend.
 *     Stable, explainable, and revisable on the row.
 */
export interface OwnedPickRef {
  round: number;
  /** The team whose slot this pick originally was — the thing that numbers it on the board. */
  from: string;
}

export function resolvePickIdentities(
  assigned: ReadonlyMap<string, number>,
  owned: readonly OwnedPickRef[],
  opts: {
    /** The declaring GM's own team, in the same naming domain as `from`. */
    self: string;
    /** player → origin, for within-round choices the manager has made by hand. */
    preferredFrom?: Readonly<Record<string, string>>;
    /** Original owner's slot within the round, once the order exists. Null before that. */
    slotInRound?: (from: string, round: number) => number | null;
  },
): Map<string, OwnedPickRef> {
  const out = new Map<string, OwnedPickRef>();
  /** Picks still unclaimed, grouped by round. */
  const pool = new Map<number, OwnedPickRef[]>();
  for (const p of owned) {
    if (!pool.has(p.round)) pool.set(p.round, []);
    pool.get(p.round)!.push(p);
  }
  const claim = (player: string, round: number, from: string): boolean => {
    const picks = pool.get(round) ?? [];
    const i = picks.findIndex((p) => p.from === from);
    if (i < 0) return false;
    out.set(player, picks[i]!);
    picks.splice(i, 1);
    return true;
  };

  // The manager's explicit origins first, same principle as the round assigner: a pin is
  // never displaced by a default the loop happened to hand out earlier.
  for (const [player, round] of assigned) {
    const want = opts.preferredFrom?.[player];
    if (want) claim(player, round, want);
  }

  // Everyone else, in a stable order so two renders of the same slate agree.
  for (const [player, round] of [...assigned].sort((a, b) => a[0].localeCompare(b[0]))) {
    if (out.has(player)) continue;
    const picks = pool.get(round) ?? [];
    if (picks.length === 0) continue; // round-level assigner already reported this
    const ranked = [...picks].sort((a, b) => {
      const sa = opts.slotInRound?.(a.from, round);
      const sb = opts.slotInRound?.(b.from, round);
      // Order known for both: later pick in the round first.
      if (sa != null && sb != null && sa !== sb) return sb - sa;
      // Order unknown: his own pick first, then a stable name order.
      if ((a.from === opts.self) !== (b.from === opts.self)) return a.from === opts.self ? -1 : 1;
      return a.from.localeCompare(b.from);
    });
    claim(player, round, ranked[0]!.from);
  }

  return out;
}

/**
 * How many slots a GM actually has to rank.
 *
 * 2020-II-1 §4 spares everyone from ranking all ten: "Note that GMs will only have to select
 * draft picks up til their lottery position. The winner of the lottery only has to provide
 * their first two desired draft positions ... The runner-up of the lottery will only have to
 * select their first three preferred draft positions. etc."
 *
 * So it is position + 1: one for every slot that could be gone before your turn, plus one
 * spare. The spare exists because the Toilet Bowl winner can be inserted ahead of you after
 * the lottery has already run (§3), pushing you back a place.
 */
export function minimumPreferences(selectionPosition: number, teams: number): number {
  return Math.min(selectionPosition + 1, teams);
}

export interface DepthReason {
  /** How many slots he actually has to rank. */
  depth: number;
  /** Why, in one line, when it is not simply his own position. Empty when it is. */
  because: string;
  /** True while a pending third-place option is what is inflating it. */
  exposed: boolean;
}

/**
 * How deep a GM has to rank when somebody else might move him — Pass 06 §4.
 *
 * THE THIRD-PLACE OPTION IS ONE-DIRECTIONAL, and that asymmetry lands here. 3rd only ever swaps
 * INTO a better position, so his own depth never changes: `ownPosition + 1` covers him. 4th is
 * the exposed party — he can be pushed from 1st to 8th with no warning and no say — so while the
 * option is pending he has to rank as though the worse of the two positions were his.
 *
 * `max`, not the counterpart's position: 4th might already hold the later of the two, in which
 * case a swap moves him FORWARD and nothing about his depth needs to change. Only the downside
 * has to be covered.
 *
 * It becomes exact the moment 3rd answers, which is why this is computed rather than stored — and
 * why the screen says WHY it changed. A required-count that silently drops from nine to two
 * reads as a bug; "Ari declined, so you only have to rank two" reads as the rule working.
 */
export function preferenceDepth(
  ownPosition: number,
  teams: number,
  exposedTo?: { counterpartPosition: number; pending: boolean; counterpartName?: string } | null,
): DepthReason {
  const own = minimumPreferences(ownPosition, teams);
  if (!exposedTo?.pending || exposedTo.counterpartPosition <= 0) {
    return { depth: own, because: '', exposed: false };
  }
  const worst = Math.max(ownPosition, exposedTo.counterpartPosition);
  const depth = minimumPreferences(worst, teams);
  if (depth <= own) {
    /*
     * He already holds the later position, so the swap can only move him forward. Nothing to
     * cover, and saying "you are exposed" when he is not would be noise.
     */
    return { depth: own, because: '', exposed: false };
  }
  const who = exposedTo.counterpartName ?? 'Third place';
  return {
    depth,
    because: `${who} may take your lottery result, which would move you to `
      + `${worst}${ordinalOf(worst)}. Rank ${depth} and you are covered either way.`,
    exposed: true,
  };
}

const ordinalOf = (n: number): string => {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return 'th';
  return ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
};

/**
 * The full 2020-II-1 selection order: run the lottery, then guarantee the Toilet Bowl winner
 * second choice unless he won it outright.
 *
 * Only the top four come from the lottery (2014-II-1 §2) — the rest follow the previous
 * season's finishing order, worst first — and the champion and runner-up fall back to the
 * last two choices if the lottery did not pull them (§3).
 */
export function selectionOrder(
  finishOrder: readonly string[],
  rng: () => number,
  toiletBowlWinnerId: string | null,
): string[] {
  return drawFor(finishOrder, rng, toiletBowlWinnerId).order;
}

export interface Draw {
  /** The ten choosing positions. Index 0 is the first choice. */
  order: string[];
  /** The four the machine produced, in the order it produced them (2014-II-1 §2). */
  top4: string[];
}

/**
 * THE DRAW AND ITS PROVENANCE, together.
 *
 * `selectionOrder` returns ten names and throws away which four came out of the lottery — and the
 * board cannot tell you afterwards. Proved on 26 August by `seedhunt.mts`: six seeds reproduce the
 * 2026 board exactly, from three different underlying draws. The same ten names in the same order
 * arise whether the Toilet Bowl winner was drawn or placed, and whether the man at the fifth
 * choosing position was drawn fourth or fell in on last season's finish.
 *
 * That is not a curiosity. The reveal draws a line under the lottery-decided block and tags the
 * row that was placed rather than won, and both of those are claims about provenance that a list
 * of ten names does not contain. Without this the line was typed at four and the tag was given out
 * by name.
 */
export function drawFor(
  finishOrder: readonly string[],
  rng: () => number,
  toiletBowlWinnerId: string | null,
): Draw {
  const top4 = runLottery(finishOrder, rng).slice(0, 4);
  return { order: boardFromDraw(finishOrder, top4, toiletBowlWinnerId), top4 };
}

/**
 * WAS HE PLACED BY THE RULE, or did he win his way into the block?
 *
 * 2020-II-1 §3 guarantees the Toilet Bowl winner the second choosing position — "if the Toilet
 * Bowl-winning GM wins the lottery, then this provision is not used for that year's draft". So the
 * tag belongs to the man who is in the lottery block WITHOUT having been drawn into it, and to
 * nobody else. It was handed out on identity alone, which meant a Toilet Bowl winner who won the
 * draw outright was still labelled "placed by rule" and had his 13% suppressed — the app
 * contradicting the one sentence of the rule that says when it does not apply.
 */
export function placedByRule(
  top4: readonly string[],
  toiletBowlWinnerId: string | null | undefined,
): boolean {
  return Boolean(toiletBowlWinnerId) && !top4.includes(toiletBowlWinnerId!);
}

/**
 * THE BOARD A GIVEN TOP FOUR PRODUCES — the whole of the order that is not the draw itself.
 *
 * Split out of `selectionOrder` on 26 August because a second module was deriving it
 * independently and getting a different answer. `revealOdds.boardFor` built
 * `[...drawn, ...restByWeight]`: no §3 tail, no Toilet Bowl lift. So from the second row of the
 * reveal onward, not one of the 5,040 enumerated outcomes matched what the room could see, the
 * live set was empty, and the odds panel — the thing the whole reveal is built around — read
 * **0.0% for every remaining GM through eight of the ten reveals**.
 *
 * Two implementations of one rule is two chances to disagree with the rulebook, and this pair
 * disagreed with each other. There is one now, and both callers take it.
 *
 * What it encodes, in order:
 *
 *   2014-II-1 §2  only the top four come out of the lottery; the other six follow last season.
 *   §3            champion and runner-up revert to the 10th and 9th choices unless the lottery
 *                 lifted them — which is why the tail is built by NAME and never by weight. They
 *                 share the lowest weight, so sorting the tail on weight leaves which of them
 *                 lands on 10 to a tie-break; here it happened to be alphabetical, and happened
 *                 to be right for VISHAL and LUKE. It would silently reverse the pair in a season
 *                 whose champion sorted first.
 *   2020-II-1 §3  the Toilet Bowl winner is lifted to the second choosing position, additively —
 *                 applied last, over everything above.
 */
export function boardFromDraw(
  finishOrder: readonly string[],
  /** The four the lottery produced, in the order it produced them. */
  top4: readonly string[],
  toiletBowlWinnerId: string | null,
): string[] {
  const drawn = [...top4];

  // Everyone else takes their turn worst-finisher-first, which is the pre-lottery default.
  const rest = [...finishOrder].reverse().filter((t) => !drawn.includes(t));

  const champion = finishOrder[0];
  const runnerUp = finishOrder[1];
  // §3 is specific about which end: the champion reverts to the 10th choice and the runner-up
  // to the 9th, so the runner-up sits immediately ahead of him.
  const tail: string[] = [];
  for (const t of [runnerUp, champion]) {
    if (t && !drawn.includes(t)) {
      const i = rest.indexOf(t);
      if (i >= 0) {
        rest.splice(i, 1);
        tail.push(t);
      }
    }
  }

  return applyToiletBowlPriority([...drawn, ...rest, ...tail], toiletBowlWinnerId);
}

/**
 * HOW DEEP THE LOTTERY BLOCK GOES on a given board — the row the reveal draws its divider under.
 *
 * Four positions come out of the machine, but the Toilet Bowl lift is ADDITIVE: it inserts a
 * fifth name into the block without displacing any of the four. So the block is five deep in a
 * normal year and four in a year the Toilet Bowl winner wins the draw outright — which is why
 * this is counted rather than typed.
 */
export function lotteryBlockDepth(
  board: readonly string[],
  top4: readonly string[],
  toiletBowlWinnerId: string | null,
): number {
  const lifted = toiletBowlWinnerId
    && !top4.includes(toiletBowlWinnerId)
    && board.includes(toiletBowlWinnerId);
  return top4.length + (lifted ? 1 : 0);
}

/**
 * The order if nothing surprising happens: the lottery goes to form and nobody exercises an
 * option.
 *
 * "To form" is simpler than it sounds. Only the top four choosing positions come out of the
 * lottery (2014-II-1 §2) and the odds rise monotonically as you finish worse, so a chalk draw
 * gives those four positions to the four worst finishers — which is exactly the worst-first
 * order the remaining six already follow. The champion and runner-up are already at the back by
 * virtue of finishing first and second.
 *
 * The Toilet Bowl lift IS applied, because 2020-II-1 §3 is automatic rather than optional. The
 * 3rd/4th swap (2018-II-5) is NOT, because it is a declared election and this is the projection
 * for "nobody elects anything".
 *
 * And because §6 hands a non-submitter the earliest slot still free, a league where nobody
 * submits a preference ends up drafting in exactly this order — so this doubles as the board's
 * honest default before the real lottery is run.
 */
export function chalkOrder(
  finishOrder: readonly string[],
  toiletBowlWinnerId?: string | null,
): string[] {
  return applyToiletBowlPriority([...finishOrder].reverse(), toiletBowlWinnerId ?? null);
}

/* -- what a manager is actually looking at (2020-II-1 §6) ------------------------------------ */

export interface SlotOutlook {
  /** What is left when your turn comes if you submit nothing at all. */
  worstCase: number | null;
  /** What your CURRENT list would get you, given what everyone ahead has submitted so far. */
  projected: number | null;
  /** How many of the ten have submitted anything. The projection is only as firm as this. */
  submitted: number;
}

export interface SlotOutlookInput {
  prefs: readonly { gm: string; ranked?: number[] }[];
  /** The selection order the lottery produced. Empty until it is run. */
  order: readonly string[];
  /** Where the viewer chooses, 1-based, or null if the order does not name him. */
  position: number | null;
  /** The viewer's list AS HE IS DRAGGING IT — not the saved one, so the number moves with him. */
  ranked: readonly number[];
  teams: number;
}

/**
 * Walk the selection order down to the viewer's turn, handing each GM ahead of him the best slot
 * on his list that is still free — and, for anybody who submitted nothing, the LOWEST free slot,
 * which is what §6 gives a non-submitter. What is left is what the viewer is choosing from.
 *
 * Pure, and deliberately so: it used to run inside a `.then()` keyed on the viewer's own list, so
 * every drag of a slot was a round trip to the database to answer a question that is arithmetic.
 */
export function slotOutlook(
  { prefs, order, position, ranked, teams }: SlotOutlookInput,
): SlotOutlook {
  const submitted = prefs.filter((p) => (p.ranked?.length ?? 0) > 0).length;
  if (position === null) return { worstCase: null, projected: null, submitted };

  const listOf = (g: string) =>
    prefs.find((p) => p.gm.toUpperCase() === g.toUpperCase())?.ranked ?? [];

  const every = Array.from({ length: teams }, (_, i) => i + 1);
  const taken = new Set<number>();
  for (const g of order.slice(0, position - 1)) {
    const pick = listOf(g).find((s) => !taken.has(s)) ?? every.find((s) => !taken.has(s));
    if (pick) taken.add(pick);
  }

  const free = every.filter((s) => !taken.has(s));
  return {
    worstCase: free[0] ?? null,
    projected: ranked.find((s) => !taken.has(s)) ?? free[0] ?? null,
    submitted,
  };
}
